// YNX — MEDIA CHECK: live preview of microphone + camera + speakers.
// Used as a "pré-vol" before joining a Jitsi meeting.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Mic, MicOff, Video, VideoOff, Volume2, RefreshCw, CheckCircle2, AlertTriangle,
} from 'lucide-react';

export const MediaCheck = ({ onReady, autoStart = true, compact = false }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const audioCtxRef = useRef(null);
  // Latest device selections — refs so the start() callback stays stable.
  const micRef = useRef('');
  const camRef = useRef('');
  const onReadyRef = useRef(onReady);
  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);

  const [devices, setDevices] = useState({ mics: [], cams: [], speakers: [] });
  const [micId, setMicId]   = useState('');
  const [camId, setCamId]   = useState('');
  const [spkId, setSpkId]   = useState('');
  const [audioLevel, setAudioLevel] = useState(0);  // 0..100
  const [hasVideo, setHasVideo] = useState(false);
  const [hasAudio, setHasAudio] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const stop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); }
      catch (err) { console.debug('YNX MediaCheck: audio ctx close failed', err); }
      audioCtxRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setHasVideo(false);
    setHasAudio(false);
    setAudioLevel(0);
  }, []);

  // `start` reads device IDs from refs → stable callback, no stale closures.
  const start = useCallback(async (mic, cam) => {
    const useMic = mic !== undefined ? mic : micRef.current;
    const useCam = cam !== undefined ? cam : camRef.current;
    setError('');
    setBusy(true);
    stop();
    try {
      const constraints = {
        audio: useMic ? { deviceId: { exact: useMic } } : true,
        video: useCam
          ? { deviceId: { exact: useCam }, width: 640, height: 360 }
          : { width: 640, height: 360 },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch((err) => {
          console.debug('YNX MediaCheck: autoplay blocked', err);
        });
      }
      const audioTracks = stream.getAudioTracks();
      const videoTracks = stream.getVideoTracks();
      setHasVideo(videoTracks.length > 0);
      setHasAudio(audioTracks.length > 0);

      // VU-meter via WebAudio analyser
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx && audioTracks.length > 0) {
        const ctx = new AudioCtx();
        audioCtxRef.current = ctx;
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        src.connect(analyser);
        const buf = new Uint8Array(analyser.fftSize);
        const tick = () => {
          analyser.getByteTimeDomainData(buf);
          let peak = 0;
          for (let i = 0; i < buf.length; i++) {
            const v = Math.abs(buf[i] - 128) / 128;
            if (v > peak) peak = v;
          }
          setAudioLevel(Math.min(100, Math.round(peak * 220)));
          rafRef.current = requestAnimationFrame(tick);
        };
        tick();
      }

      // Refresh device list once we have permission (labels become populated)
      try {
        const list = await navigator.mediaDevices.enumerateDevices();
        const mics = list.filter((d) => d.kind === 'audioinput');
        const cams = list.filter((d) => d.kind === 'videoinput');
        const speakers = list.filter((d) => d.kind === 'audiooutput');
        setDevices({ mics, cams, speakers });

        const settledMic = (audioTracks[0]?.getSettings()?.deviceId) || '';
        const settledCam = (videoTracks[0]?.getSettings()?.deviceId) || '';
        if (!micRef.current && settledMic) {
          micRef.current = settledMic;
          setMicId(settledMic);
        }
        if (!camRef.current && settledCam) {
          camRef.current = settledCam;
          setCamId(settledCam);
        }
        setSpkId((prev) => prev || (speakers[0]?.deviceId || ''));
      } catch (err) {
        console.debug('YNX MediaCheck: enumerateDevices failed', err);
      }

      onReadyRef.current?.({
        micId: useMic || (audioTracks[0]?.getSettings()?.deviceId) || '',
        camId: useCam || (videoTracks[0]?.getSettings()?.deviceId) || '',
        audioOK: audioTracks.length > 0,
        videoOK: videoTracks.length > 0,
      });
    } catch (e) {
      setError(e?.message || 'Accès micro/caméra refusé.');
    } finally {
      setBusy(false);
    }
  }, [stop]);

  useEffect(() => {
    if (autoStart) start();
    return () => stop();
  }, [autoStart, start, stop]);

  const handleMicChange = (e) => {
    const v = e.target.value;
    micRef.current = v;
    setMicId(v);
    start(v, camRef.current);
  };
  const handleCamChange = (e) => {
    const v = e.target.value;
    camRef.current = v;
    setCamId(v);
    start(micRef.current, v);
  };

  const audioOK = hasAudio && audioLevel > 2;
  const videoOK = hasVideo;

  return (
    <div className={`media-check ${compact ? 'compact' : ''}`} data-testid='media-check'>
      <div className='media-preview'>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video ref={videoRef} muted playsInline data-testid='media-preview-video' />
        {!hasVideo && !busy && (
          <div className='media-preview-empty'>
            <VideoOff size={36} />
            <span>Caméra inactive</span>
          </div>
        )}
        {busy && <div className='media-preview-empty'>Initialisation…</div>}
      </div>

      <div className='media-status-row'>
        <div className={`media-status ${audioOK ? 'ok' : (hasAudio ? 'warn' : 'ko')}`} data-testid='media-mic-status'>
          {audioOK ? <Mic size={14} /> : <MicOff size={14} />}
          <span>Micro</span>
          {audioOK
            ? <CheckCircle2 size={14} className='ok-icon' />
            : <AlertTriangle size={14} className='warn-icon' />}
        </div>
        <div className={`media-status ${videoOK ? 'ok' : 'ko'}`} data-testid='media-cam-status'>
          {videoOK ? <Video size={14} /> : <VideoOff size={14} />}
          <span>Caméra</span>
          {videoOK
            ? <CheckCircle2 size={14} className='ok-icon' />
            : <AlertTriangle size={14} className='warn-icon' />}
        </div>
      </div>

      {hasAudio && (
        <div className='media-vu' aria-label='Niveau du micro'>
          <div className='media-vu-bar' style={{ width: `${audioLevel}%` }} />
        </div>
      )}

      {!compact && (
        <div className='media-selects'>
          <label className='biz-label'>Microphone</label>
          <select
            className='biz-input'
            value={micId}
            onChange={handleMicChange}
            data-testid='media-select-mic'
          >
            {devices.mics.length === 0 && <option value=''>— par défaut —</option>}
            {devices.mics.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>{d.label || 'Micro sans nom'}</option>
            ))}
          </select>
          <label className='biz-label'>Caméra</label>
          <select
            className='biz-input'
            value={camId}
            onChange={handleCamChange}
            data-testid='media-select-cam'
          >
            {devices.cams.length === 0 && <option value=''>— par défaut —</option>}
            {devices.cams.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>{d.label || 'Caméra sans nom'}</option>
            ))}
          </select>
          {devices.speakers.length > 0 && (
            <>
              <label className='biz-label'><Volume2 size={12} /> Haut-parleurs</label>
              <select
                className='biz-input'
                value={spkId}
                onChange={(e) => setSpkId(e.target.value)}
                data-testid='media-select-spk'
              >
                {devices.speakers.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>{d.label || 'Sortie sans nom'}</option>
                ))}
              </select>
            </>
          )}
        </div>
      )}

      <div className='media-actions'>
        <button className='biz-btn-ghost sm' onClick={() => start()} disabled={busy} data-testid='media-restart-btn'>
          <RefreshCw size={12} /> Tester à nouveau
        </button>
      </div>

      {error && (
        <div className='biz-error' data-testid='media-error'>
          <AlertTriangle size={12} /> {error}
        </div>
      )}
    </div>
  );
};

export default MediaCheck;
